import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useFetchPrincipalEntitiesdataQuery } from "../store/services/principleApi";
import CommonModal from "../component/common/commonModal";
import EditIcon from "@mui/icons-material/Edit";
import { cellSX, layoutCSS } from "../component/common/commonStyle";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useCreateHeaderManagementMutation,
  useFetchHeadersByManagementQuery,
  useUpdateHeaderManagementMutation,
  useDeleteHeaderManagementMutation,
} from "../store/services/headerApi ";
import { toast } from "react-toastify";
import { maskNumber } from "../component/common/Encryption";

const CreateHeader = () => {
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [errors, setErrors] = useState({});
  const [openForm, setOpenForm] = useState(false);
  const [headerType, setHeaderType] = useState("");
  const [editingHeader, setEditingHeader] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [headerToDelete, setHeaderToDelete] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [createHeader] = useCreateHeaderManagementMutation();
  const [updateHeader] = useUpdateHeaderManagementMutation();
  const [deleteHeader] = useDeleteHeaderManagementMutation();

  const {
    data: entityData,
    isLoading: isEntityLoading,
    isError: isEntityError,
  } = useFetchPrincipalEntitiesdataQuery();
  const principalEntities = entityData?.data || [];

  const { data: headersData, refetch } =
    useFetchHeadersByManagementQuery(selectedEntityId);

  const handleEntityChange = (e) => {
    const selectedId = e.target.value;
    setSelectedEntityId(selectedId);
    refetch();
  };

  useEffect(() => {
    if (headersData?.data) {
      setHeaders(headersData.data);
    }
  }, [headersData]);

  const handleEdit = (header) => {
    setEditingHeader(header);
    setHeaderName(header.header_name);
    setHeaderType(
      /^\d+$/.test(header.header_name) ? "Promotional" : "Transactional"
    );
    setOpenForm(true);
  };

  const handleDelete = (id) => {
    setHeaderToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await deleteHeader(headerToDelete).unwrap();
      toast.success("Header deleted successfully");
      setHeaders((prev) => prev.filter((h) => h.header_id !== headerToDelete));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete header");
    } finally {
      setDeleteConfirmOpen(false);
      setHeaderToDelete(null);
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!headerName || !selectedEntityId) {
      newErrors.header = "Header name and Principal Entity are required.";
    }

    if (headerType === "Promotional" && !/^\d+$/.test(headerName)) {
      newErrors.header = "For Promotional, header name must be numeric.";
    }

    if (headerType === "Transactional" && !/^[a-zA-Z]+$/.test(headerName)) {
      newErrors.header = "For Transactional, header name must be alphabetic.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const headerTypeValue = (headerType || "").toLowerCase();
      // map UI selection to API header_type
      const header_type = headerTypeValue === "promotional" ? "promotional" : "explicit";

      if (editingHeader) {
        await updateHeader({
          header_id: editingHeader.header_id,
          header_name: headerName,
          header_type,
        }).unwrap();
        toast.success("Header updated successfully");
      } else {
        await createHeader({
          header_name: headerName,
          principalentity_id: selectedEntityId,
          header_type,
        }).unwrap();
        toast.success("Header created successfully");
      }

      setOpenForm(false);
      setHeaderName("");
      setErrors({});
      setHeaderType("");
      setEditingHeader(null);
      refetch();
    } catch (err) {
      setErrors({ header: err?.data?.message || "Error saving header." });
    }
  };

  const principalEntityMap = principalEntities.reduce((acc, entity) => {
    acc[entity.principalentity_id] = ` ${entity.pe_id}`;
    return acc;
  }, {});

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          fontSize: "0.8rem",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        Manage Header
        <Button
          variant="contained"
          sx={{ width: "150px", marginBottom: "20px", fontSize: "0.7rem" }}
          onClick={() => setOpenForm(true)}
        >
          New Header
        </Button>
      </Typography>

      <CommonModal open={openForm} setOpen={setOpenForm}>
        {errors.header && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 1, fontSize: "0.75rem" }}
          >
            {errors.header}
          </Typography>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel id="entity-label" sx={{ fontSize: "0.75rem" }}>
            Principal Entity
          </InputLabel>

          <Select
            labelId="entity-label"
            id="entity-select"
            value={selectedEntityId || ""} 
            label="Principal Entity"
            onChange={handleEntityChange}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "6px 8px", 
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
              Select Entity
            </MenuItem>

            {isEntityLoading ? (
              <MenuItem disabled sx={{ fontSize: "0.75rem" }}>
                Loading...
              </MenuItem>
            ) : isEntityError ? (
              <MenuItem disabled sx={{ fontSize: "0.75rem" }}>
                Error loading entities
              </MenuItem>
            ) : (
              (principalEntities || []).map((entity) => (
                <MenuItem
                  key={entity.principalentity_id}
                  value={entity.principalentity_id} 
                  sx={{ fontSize: "0.75rem" }}
                >
                  {maskNumber(entity?.pe_id)} 
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <RadioGroup
            value={headerType}
            onChange={(e) => setHeaderType(e.target.value)}
            row
          >
            <FormControlLabel
              value="Promotional"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: "0.75rem" }}>
                  Promotional
                </Typography>
              }
            />
            <FormControlLabel
              value="Transactional"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: "0.75rem" }}>
                  Transactional
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        <TextField
          label="Header Name"
          variant="outlined"
          fullWidth
          required
          value={headerName}
          onChange={(e) => setHeaderName(e.target.value)}
          disabled={!headerType}
          sx={{
            mb: 2,
            fontSize: "0.75rem",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "10px 10px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.75rem",
            },
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={() => setOpenForm(false)}
            variant="outlined"
            color="error"
            size="small"
            sx={{ fontSize: "0.75rem", width: "30%" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="small"
            disabled={!headerType}
            sx={{ fontSize: "0.75rem", width: "30%" }}
          >
            Submit
          </Button>
        </Box>
      </CommonModal>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                fontWeight: "bold",
                color: "white",
                // textTransform: 'uppercase',
                fontSize: "0.75rem",
                padding: "6px",
                borderBottom: "0.5px solid #B0B0B0",
                borderRight: "0.5px solid #B0B0B0",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: "30px" }}>Header ID</TableCell>
              <TableCell sx={{ width: "30px" }}>Header Name</TableCell>
              <TableCell sx={{ width: "30px" }}>PE ID</TableCell>
              <TableCell sx={{ width: "30px" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {headers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  No headers found for this entity.
                </TableCell>
              </TableRow>
            ) : (
              headers.map((header) => (
                <TableRow key={header.header_id}>
                  <TableCell sx={cellSX}>{header.header_id}</TableCell>
                  <TableCell sx={cellSX}>{header.header_name}</TableCell>
                  <TableCell sx={cellSX}>
                    {principalEntityMap[header.principalentity_id]
                      ? maskNumber(
                          principalEntityMap[header.principalentity_id]
                        )
                      : "N/A"}
                  </TableCell>

                  <TableCell sx={cellSX}>
                    <EditIcon
                      sx={{ cursor: "pointer", mr: 1 }}
                      onClick={() => handleEdit(header)}
                    />
                    <DeleteIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleDelete(header.header_id)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CommonModal open={deleteConfirmOpen} setOpen={setDeleteConfirmOpen}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: "0.9rem" }}>
          Are you sure you want to delete this header?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={handleDeleteConfirmed}
            variant="contained"
            color="error"
            size="small"
            sx={{ fontSize: "0.75rem", width: "30%" }}
          >
            Delete
          </Button>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            color="primary"
            size="small"
            sx={{ fontSize: "0.75rem", width: "30%" }}
          >
            Cancel
          </Button>
        </Box>
      </CommonModal>
    </Box>
  );
};

export default CreateHeader;
